import React, { useEffect, useState } from "react";
import { Form, Row } from "react-bootstrap";
import { apiUrls } from "../../config/apiUrls.const";
import axios from "axios";

interface LocationSelectorProps {
  division_id: string;
  district_id: string;
  thana_id: string;
  postal_code: string;
  onChange: (name: string, value: string) => void;
  required?: boolean;
  setClass?: string; // Made optional with default
  errors?: Record<string, string>; // ✅ Added errors prop
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  division_id,
  district_id,
  thana_id,
  postal_code,
  onChange,
  required = false,
  setClass = "col-sm-3",
  errors = {}, // Default to empty object
}) => {
  const [data, setData] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [thanas, setThanas] = useState<any[]>([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get(apiUrls.locations, {
          headers: {
            "Content-Type": "application/json"
          }
        });
        const locationData = res.data;
        setData(locationData);
        setDivisions(
          locationData.map((d: any) => ({
            id: d.id,
            label: d.name_en
          }))
        );
      } catch (error) {
        console.error("Location API error:", error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    const selectedDiv = data.find((d) => d.id === division_id);
    setDistricts(selectedDiv?.districts || []);

    if (!division_id) {
      setDistricts([]);
      setThanas([]);
    }
  }, [division_id, data]);

  useEffect(() => {
    const selectedDiv = data.find((d) => d.id === division_id);
    const selectedDist = selectedDiv?.districts.find((dist: any) => dist.id === district_id);
    setThanas(selectedDist?.thanas || []);

    if (!district_id) setThanas([]);
  }, [district_id, division_id, data]);

  const renderError = (fieldName: string) => {
    return errors[fieldName] ? (
      <div className="invalid-feedback d-block">{errors[fieldName]}</div>
    ) : null;
  };

  return (
    <Row>
      {/* Division */}
      <div className={setClass}>
        <Form.Group className="mb-3">
          <Form.Label>Division {required && <span className="text-danger">*</span>}</Form.Label>
          <Form.Select
            className={errors.division_id ? "is-invalid" : ""}
            value={division_id || ""}
            onChange={(e) => onChange("division_id", e.target.value)}
          >
            <option value="">Select Division</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </Form.Select>
          {renderError("division_id")}
        </Form.Group>
      </div>

      {/* District */}
      <div className={setClass}>
        <Form.Group className="mb-3">
          <Form.Label>District {required && <span className="text-danger">*</span>}</Form.Label>
          <Form.Select
            className={errors.district_id ? "is-invalid" : ""}
            value={district_id || ""}
            onChange={(e) => onChange("district_id", e.target.value)}
            disabled={!division_id}
          >
            <option value="">Select District</option>
            {districts.map((d) => <option key={d.id} value={d.id}>{d.name_en}</option>)}
          </Form.Select>
          {renderError("district_id")}
        </Form.Group>
      </div>

      {/* Thana */}
      <div className={setClass}>
        <Form.Group className="mb-3">
          <Form.Label>Thana {required && <span className="text-danger">*</span>}</Form.Label>
          <Form.Select
            className={errors.thana_id ? "is-invalid" : ""}
            value={thana_id || ""}
            onChange={(e) => onChange("thana_id", e.target.value)}
            disabled={!district_id}
          >
            <option value="">Select Thana</option>
            {thanas.map((t) => <option key={t.id} value={t.id}>{t.name_en}</option>)}
          </Form.Select>
          {renderError("thana_id")}
        </Form.Group>
      </div>

      {/* Postal Code */}
      <div className={setClass}>
        <Form.Group className="mb-3">
          <Form.Label>Postal Code {required && <span className="text-danger">*</span>}</Form.Label>
          <Form.Control
            type="text"
            className={errors.postal_code ? "is-invalid" : ""}
            value={postal_code || ""}
            onChange={(e) => onChange("postal_code", e.target.value)}
          />
          {renderError("postal_code")}
        </Form.Group>
      </div>
    </Row>
  );
};

export default LocationSelector;